import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSearchParams } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Search, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, MapPin, FileCheck, Clock, CheckCircle, AlertCircle, X, Link2, Download, FileSpreadsheet, FileText, Upload, Loader2, Filter, Sparkles, Eye, EyeOff, Zap, Hash, FolderOpen, HardDrive, Cloud, Mail, Copy, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from '../components/ui/dropdown-menu';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

// Column configuration for visibility selection
const COLUMN_CONFIG = {
  permit_id: { label: 'Application ID', default: true },
  address: { label: 'Address', default: true },
  aka: { label: 'AKA', default: true },
  block_lot: { label: 'Block/Lot', default: true },
  job_type: { label: 'Job Type', default: true },
  work_type: { label: 'Work Type', default: true },
  description: { label: 'Description', default: true },
  estimated_cost: { label: 'Est. Cost', default: true },
  issue_date: { label: 'Filed Date', default: true },
  status: { label: 'Status', default: true }
};

// Sort indicator component - moved outside to prevent recreation on each render
const SortIndicator = ({ column, sortConfig }) => {
  if (sortConfig.column !== column) {
    return <ChevronUp className="w-3 h-3 opacity-30" />;
  }
  return sortConfig.direction === 'asc' 
    ? <ChevronUp className="w-3 h-3 text-cyan-400" />
    : <ChevronDown className="w-3 h-3 text-cyan-400" />;
};

export default function ApplicationsPage() {
  const { user, api } = useAuth();
  const [searchParams] = useSearchParams();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(200);

  const pageSizeOptions = [200, 400, 600, 800, 1000];

  // Check if quicksearch param is present to show dialog
  const quickSearchParam = searchParams.get('quicksearch') === 'true';

  // Quick Search Dialog state - show on first load or when quicksearch param is true
  const [showQuickSearch, setShowQuickSearch] = useState(true);
  const [quickSearchAppId, setQuickSearchAppId] = useState('');
  const [quickSearching, setQuickSearching] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const cols = {};
    Object.entries(COLUMN_CONFIG).forEach(([key, config]) => {
      cols[key] = config.default;
    });
    return cols;
  });

  // Sorting state
  const [sortConfig, setSortConfig] = useState({ column: null, direction: 'asc' });

  // Handle column sort
  const handleSort = (column) => {
    setSortConfig(prev => ({
      column,
      direction: prev.column === column && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Show quick search when navigating with quicksearch param
  useEffect(() => {
    if (quickSearchParam) {
      setShowQuickSearch(true);
    }
  }, [quickSearchParam]);

  // Application datasets (permits, DOB NOW filings, and Certificate of Occupancy)
  const APP_DATASETS = {
    permits: 'DOB Permits',
    dob_now: 'DOB NOW Filings',
    certificate_of_occupancy: 'Certificate of Occupancy'
  };

  const [filters, setFilters] = useState({
    dataset: 'permits',
    address: '',
    block: '',
    lot: '',
    borough: 'all',
    status: 'all',
    jobType: 'all',
    workType: 'all'
  });

  // Pivot filter state - for quick filtering
  const [pivotMode, setPivotMode] = useState('jobType'); // 'jobType' or 'workType'
  const [activePivot, setActivePivot] = useState('all');

  // Address autocomplete state
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const addressInputRef = useRef(null);
  
  // Export state
  const [exporting, setExporting] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState('csv');
  const [exportLocation, setExportLocation] = useState('download'); // 'download', 'choose', 'email'
  const [exportEmail, setExportEmail] = useState('');
  const [exportScope, setExportScope] = useState('page'); // 'page' or 'all'
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [uploadedTemplate, setUploadedTemplate] = useState(null);
  const fileInputRef = useRef(null);
  const suggestionsRef = useRef(null);
  
  // Check if File System Access API is supported
  const supportsFilePicker = typeof window !== 'undefined' && 'showSaveFilePicker' in window;

  // Store raw data for pivot calculations
  const [rawApplications, setRawApplications] = useState([]);

  const fetchApplications = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        dataset: filters.dataset,
        offset: String(page * pageSize),
        limit: String(pageSize)
      });
      
      if (filters.address) params.append('address', filters.address);
      if (filters.block) params.append('block', filters.block);
      if (filters.lot) params.append('lot', filters.lot);
      if (filters.borough && filters.borough !== 'all') params.append('borough', filters.borough);

      const res = await api().get(`/violations/search?${params}`);
      let data = res.data.data || [];
      
      // Store raw data for pivot calculations
      setRawApplications(data);
      
      // Filter by status client-side if needed
      if (filters.status && filters.status !== 'all') {
        data = data.filter(app => app.status === filters.status);
      }
      
      setApplications(data);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error('Failed to fetch applications:', err);
    } finally {
      setLoading(false);
    }
  }, [api, page, pageSize, filters.dataset, filters.address, filters.block, filters.lot, filters.borough, filters.status]);

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  // Calculate pivot counts from raw data
  const pivotCounts = useMemo(() => {
    const jobTypes = {};
    const workTypes = {};
    
    rawApplications.forEach(app => {
      const jt = app.job_type || 'Unknown';
      const wt = app.work_type || 'Unknown';
      jobTypes[jt] = (jobTypes[jt] || 0) + 1;
      workTypes[wt] = (workTypes[wt] || 0) + 1;
    });
    
    return { jobTypes, workTypes };
  }, [rawApplications]);

  // Get sorted pivot options (by count, descending)
  const pivotOptions = useMemo(() => {
    const counts = pivotMode === 'jobType' ? pivotCounts.jobTypes : pivotCounts.workTypes;
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10); // Top 10 types
  }, [pivotMode, pivotCounts]);

  // Filter applications by active pivot
  const filteredApplications = useMemo(() => {
    if (activePivot === 'all') return applications;
    
    return applications.filter(app => {
      if (pivotMode === 'jobType') {
        return (app.job_type || 'Unknown') === activePivot;
      } else {
        return (app.work_type || 'Unknown') === activePivot;
      }
    });
  }, [applications, activePivot, pivotMode]);

  // Sort filtered applications
  const sortedApplications = useMemo(() => {
    if (!sortConfig.column) return filteredApplications;
    
    return [...filteredApplications].sort((a, b) => {
      let aVal, bVal;
      
      switch (sortConfig.column) {
        case 'permit_id':
          aVal = a.permit_id || '';
          bVal = b.permit_id || '';
          break;
        case 'address':
          aVal = a.address || '';
          bVal = b.address || '';
          break;
        case 'block_lot':
          aVal = `${a.block || ''}-${a.lot || ''}`;
          bVal = `${b.block || ''}-${b.lot || ''}`;
          break;
        case 'job_type':
          aVal = a.job_type || '';
          bVal = b.job_type || '';
          break;
        case 'work_type':
          aVal = a.work_type || '';
          bVal = b.work_type || '';
          break;
        case 'description':
          aVal = a.description || '';
          bVal = b.description || '';
          break;
        case 'estimated_cost':
          // Parse estimated_cost - handle both string with $ and numeric values
          const aCost = String(a.estimated_cost || '0').replace(/[$,]/g, '');
          const bCost = String(b.estimated_cost || '0').replace(/[$,]/g, '');
          aVal = parseFloat(aCost) || 0;
          bVal = parseFloat(bCost) || 0;
          break;
        case 'issue_date':
          aVal = a.issue_date ? new Date(a.issue_date).getTime() : 0;
          bVal = b.issue_date ? new Date(b.issue_date).getTime() : 0;
          break;
        case 'status':
          aVal = a.status || '';
          bVal = b.status || '';
          break;
        default:
          return 0;
      }
      
      // Handle numeric comparison
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
      }
      
      // Handle string comparison
      if (typeof aVal === 'string') {
        return sortConfig.direction === 'asc' 
          ? aVal.localeCompare(bVal) 
          : bVal.localeCompare(aVal);
      }
      
      return 0;
    });
  }, [filteredApplications, sortConfig]);

  // Handle pivot click
  const handlePivotClick = (value) => {
    setActivePivot(value);
    setPage(0);
  };

  // Quick search by application ID
  const handleQuickSearch = async () => {
    if (!quickSearchAppId.trim()) {
      toast.error('Please enter an application number');
      return;
    }
    
    setQuickSearching(true);
    try {
      // Search for the specific application ID
      const params = new URLSearchParams({
        dataset: filters.dataset,
        limit: '50'
      });
      
      // The permit_id/application number search
      const res = await api().get(`/applications/search?app_id=${quickSearchAppId.trim()}&${params}`);
      
      if (res.data.data && res.data.data.length > 0) {
        setApplications(res.data.data);
        setRawApplications(res.data.data);
        setTotal(res.data.data.length);
        setShowQuickSearch(false);
        toast.success(`Found ${res.data.data.length} matching application(s)`);
      } else {
        // Fallback: do a general search and filter client-side
        const fallbackRes = await api().get(`/violations/search?dataset=permits&limit=500`);
        const allData = fallbackRes.data.data || [];
        const matched = allData.filter(app => 
          (app.permit_id || '').toString().includes(quickSearchAppId.trim()) ||
          (app.job_filing_number || '').toString().includes(quickSearchAppId.trim())
        );
        
        if (matched.length > 0) {
          setApplications(matched);
          setRawApplications(matched);
          setTotal(matched.length);
          setShowQuickSearch(false);
          toast.success(`Found ${matched.length} matching application(s)`);
        } else {
          toast.error('No applications found with that ID');
        }
      }
    } catch (err) {
      console.error('Quick search error:', err);
      toast.error('Search failed. Please try again.');
    } finally {
      setQuickSearching(false);
    }
  };

  // Skip quick search and show all applications
  const skipQuickSearch = () => {
    setShowQuickSearch(false);
    fetchApplications();
  };

  // Toggle column visibility
  const toggleColumn = (columnKey) => {
    setVisibleColumns(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey]
    }));
  };

  // Live address lookup using ZOLA/GeoSearch API
  const fetchAddressSuggestions = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setAddressSuggestions([]);
      return;
    }
    
    setLoadingSuggestions(true);
    try {
      // Use ZOLA/GeoSearch API for NYC address lookup
      const params = new URLSearchParams({ query });
      if (filters.borough && filters.borough !== 'all') {
        params.append('borough', filters.borough);
      }
      
      const res = await api().get(`/address/search?${params}`);
      const suggestions = res.data.suggestions || [];
      
      setAddressSuggestions(suggestions);
      setShowSuggestions(suggestions.length > 0);
    } catch (err) {
      console.error('Failed to fetch suggestions:', err);
    } finally {
      setLoadingSuggestions(false);
    }
  }, [api, filters.borough]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (filters.address) {
        fetchAddressSuggestions(filters.address);
      } else {
        setAddressSuggestions([]);
        setShowSuggestions(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [filters.address, fetchAddressSuggestions]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target) &&
          addressInputRef.current && !addressInputRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectAddress = (suggestion) => {
    setFilters({
      ...filters,
      address: suggestion.address,
      block: suggestion.block || '',
      lot: suggestion.lot || ''
    });
    setShowSuggestions(false);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0);
    fetchApplications();
  };

  // Helper function to save file with "Save As" dialog or direct download
  const saveFile = async (blob, suggestedName, fileTypes) => {
    // Check if File System Access API is supported (for "Choose Location" option)
    if (exportLocation === 'choose' && 'showSaveFilePicker' in window) {
      try {
        const handle = await window.showSaveFilePicker({
          suggestedName,
          types: fileTypes
        });
        const writable = await handle.createWritable();
        await writable.write(blob);
        await writable.close();
        return true;
      } catch (err) {
        if (err.name === 'AbortError') {
          // User cancelled the save dialog
          return false;
        }
        // Fallback to regular download if API fails
        console.warn('File System Access API failed, falling back to download:', err);
      }
    }
    
    // Regular download (fallback or "download" option)
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = suggestedName;
    link.click();
    URL.revokeObjectURL(url);
    return true;
  };

  // Fetch export data
  const fetchExportData = async () => {
    // If exporting current page only, use the already-loaded data
    if (exportScope === 'page') {
      return applications; // Return currently displayed records
    }
    
    // For "all" scope, fetch up to 1000 records from API
    const params = new URLSearchParams();
    params.append('dataset', filters.dataset);
    params.append('limit', '1000');
    params.append('offset', '0');
    if (filters.address) params.append('address', filters.address);
    if (filters.block) params.append('block', filters.block);
    if (filters.lot) params.append('lot', filters.lot);
    if (filters.borough !== 'all') params.append('borough', filters.borough);
    
    const res = await api().get(`/violations/search?${params.toString()}`);
    return res.data.data || [];
  };

  // Main export handler - called from the export dialog
  const handleExport = async () => {
    if (total === 0) {
      toast.error('No data to export');
      return;
    }
    
    // Validate email if email export selected
    if (exportLocation === 'email') {
      if (!exportEmail || !exportEmail.includes('@')) {
        toast.error('Please enter a valid email address');
        return;
      }
    }
    
    setExporting(true);
    setExportDialogOpen(false);
    
    try {
      if (exportLocation === 'email') {
        await exportViaEmail();
      } else if (exportFormat === 'csv') {
        await exportToCSV();
      } else if (exportFormat === 'xls') {
        await exportToXLS();
      } else if (exportFormat === 'pdf') {
        await exportToPDF();
      }
    } catch (err) {
      console.error('Export failed:', err);
      toast.error('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  // Export via Email
  const exportViaEmail = async () => {
    const data = await fetchExportData();
    
    if (data.length === 0) {
      toast.error('No data to export');
      return;
    }
    
    try {
      // Send export request to backend
      const res = await api().post('/export/email', {
        email: exportEmail,
        format: exportFormat,
        filters: {
          dataset: filters.dataset,
          address: filters.address,
          block: filters.block,
          lot: filters.lot,
          borough: filters.borough
        },
        recordCount: data.length
      });
      
      toast.success(`Export will be sent to ${exportEmail}`);
    } catch (err) {
      console.error('Email export failed:', err);
      toast.error('Failed to send export via email');
    }
  };

  // Export to CSV
  const exportToCSV = async () => {
    const data = await fetchExportData();
    
    if (data.length === 0) {
      toast.error('No data to export');
      return;
    }
    
    // Create CSV content
    const headers = ['Application ID', 'Address', 'AKA', 'Block', 'Lot', 'Job Type', 'Work Type', 'Description', 'Est. Cost', 'Filed Date', 'Status'];
    const csvRows = [headers.join(',')];
    
    data.forEach(row => {
      const values = [
        `"${row.permit_id || ''}"`,
        `"${(row.address || '').replace(/"/g, '""')}"`,
        `"${row.is_aka ? 'Yes' : 'No'}"`,
        `"${row.block || ''}"`,
        `"${row.lot || ''}"`,
        `"${row.job_type || ''}"`,
        `"${row.work_type || ''}"`,
        `"${(row.description || '').replace(/"/g, '""')}"`,
        `"${row.estimated_cost || ''}"`,
        `"${row.issue_date || ''}"`,
        `"${row.status || ''}"`
      ];
      csvRows.push(values.join(','));
    });
    
    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const fileName = `applications_export_${new Date().toISOString().split('T')[0]}.csv`;
    
    const saved = await saveFile(blob, fileName, [
      { description: 'CSV Files', accept: { 'text/csv': ['.csv'] } }
    ]);
    
    if (saved) {
      toast.success(`Exported ${data.length} records to CSV`);
    }
  };

  // Export to XLS (Excel)
  const exportToXLS = async () => {
    const data = await fetchExportData();
    
    if (data.length === 0) {
      toast.error('No data to export');
      return;
    }
    
    // Create a simple XLS format (HTML table that Excel can open)
    const headers = ['Application ID', 'Address', 'AKA', 'Block', 'Lot', 'Job Type', 'Work Type', 'Description', 'Est. Cost', 'Filed Date', 'Status'];
    
    let xlsContent = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel">
      <head>
        <meta charset="UTF-8">
        <!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet>
        <x:Name>Applications</x:Name>
        <x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions>
        </x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]-->
        <style>
          table { border-collapse: collapse; }
          th { background-color: #1e3a5f; color: white; font-weight: bold; padding: 8px; border: 1px solid #ccc; }
          td { padding: 6px; border: 1px solid #ccc; }
          tr:nth-child(even) { background-color: #f5f5f5; }
        </style>
      </head>
      <body>
        <table>
          <thead><tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr></thead>
          <tbody>
    `;
    
    data.forEach(row => {
      xlsContent += `<tr>
        <td>${row.permit_id || ''}</td>
        <td>${row.address || ''}</td>
        <td>${row.is_aka ? 'Yes' : 'No'}</td>
        <td>${row.block || ''}</td>
        <td>${row.lot || ''}</td>
        <td>${row.job_type || ''}</td>
        <td>${row.work_type || ''}</td>
        <td>${row.description || ''}</td>
        <td>${row.estimated_cost || ''}</td>
        <td>${row.issue_date || ''}</td>
        <td>${row.status || ''}</td>
      </tr>`;
    });
    
    xlsContent += '</tbody></table></body></html>';
    
    const blob = new Blob([xlsContent], { type: 'application/vnd.ms-excel' });
    const fileName = `applications_export_${new Date().toISOString().split('T')[0]}.xls`;
    
    const saved = await saveFile(blob, fileName, [
      { description: 'Excel Files', accept: { 'application/vnd.ms-excel': ['.xls'] } }
    ]);
    
    if (saved) {
      toast.success(`Exported ${data.length} records to Excel`);
    }
  };

  // Export to PDF
  const exportToPDF = async () => {
    const params = new URLSearchParams();
    params.append('dataset', filters.dataset);
    params.append('limit', '100');
    if (filters.address) params.append('address', filters.address);
    if (filters.block) params.append('block', filters.block);
    if (filters.lot) params.append('lot', filters.lot);
    if (filters.borough !== 'all') params.append('borough', filters.borough);
    
    const res = await api().get(`/export/pdf?${params.toString()}`, { responseType: 'blob' });
    
    const blob = new Blob([res.data], { type: 'application/pdf' });
    const fileName = `applications_report_${new Date().toISOString().split('T')[0]}.pdf`;
    
    const saved = await saveFile(blob, fileName, [
      { description: 'PDF Files', accept: { 'application/pdf': ['.pdf'] } }
    ]);
    
    if (saved) {
      toast.success('PDF report downloaded');
    }
  };

  // Legacy export functions for backward compatibility
  const exportToExcel = () => {
    setExportFormat('csv');
    setExportDialogOpen(true);
  };

  // Handle template upload
  const handleTemplateUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.match(/\.(xlsx|xls|csv)$/i)) {
        toast.error('Please upload an Excel or CSV file');
        return;
      }
      setUploadedTemplate(file);
      toast.success(`Template "${file.name}" loaded`);
    }
  };

  // Export with custom template
  const exportWithTemplate = async () => {
    if (!uploadedTemplate) {
      toast.error('Please upload a template first');
      return;
    }
    
    setExporting(true);
    try {
      const formData = new FormData();
      formData.append('template', uploadedTemplate);
      formData.append('dataset', filters.dataset);
      if (filters.block) formData.append('block', filters.block);
      if (filters.lot) formData.append('lot', filters.lot);
      if (filters.address) formData.append('address', filters.address);
      
      const res = await api().post('/export/with-template', formData, {
        responseType: 'blob',
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const fileName = `custom_export_${new Date().toISOString().split('T')[0]}.xlsx`;
      
      const saved = await saveFile(blob, fileName, [
        { description: 'Excel Files', accept: { 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'] } }
      ]);
      
      if (saved) {
        toast.success('Custom export downloaded');
        setTemplateDialogOpen(false);
      }
    } catch (err) {
      console.error('Template export failed:', err);
      toast.error('Failed to export with template');
    } finally {
      setExporting(false);
    }
  };

  const totalPages = Math.ceil(total / pageSize);

  const handlePageSizeChange = (newSize) => {
    setPageSize(Number(newSize));
    setPage(0); // Reset to first page when changing page size
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Approved':
        return <CheckCircle className="w-4 h-4 text-emerald-400" />;
      case 'In Progress':
      case 'Plan Exam':
      case 'Under Review':
        return <Clock className="w-4 h-4 text-amber-400" />;
      default:
        return <AlertCircle className="w-4 h-4 text-zinc-400" />;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Approved':
        return 'badge-success';
      case 'In Progress':
      case 'Plan Exam':
      case 'Under Review':
        return 'badge-warning';
      default:
        return 'badge-info';
    }
  };

  return (
    <DashboardLayout>
      {/* Quick Search Dialog - Prominent on page load */}
      <Dialog open={showQuickSearch} onOpenChange={setShowQuickSearch}>
        <DialogContent className="sm:max-w-lg bg-zinc-900/95 backdrop-blur-xl border-indigo-500/30 shadow-2xl shadow-indigo-500/20">
          {/* Decorative gradient background */}
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-600/10 via-transparent to-purple-600/10 rounded-lg pointer-events-none" />
          <div className="absolute -top-20 -right-20 w-40 h-40 bg-indigo-500/20 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-purple-500/20 rounded-full blur-3xl pointer-events-none" />
          
          <DialogHeader className="relative">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2.5 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl shadow-lg shadow-indigo-500/30">
                <Zap className="w-6 h-6 text-white" />
              </div>
              <div>
                <DialogTitle className="text-xl font-bold text-white">Quick Application Search</DialogTitle>
                <p className="text-sm text-zinc-400">Find applications instantly by ID</p>
              </div>
            </div>
          </DialogHeader>
          
          <div className="relative space-y-6 py-4">
            {/* Application ID Search */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-zinc-300 flex items-center gap-2">
                <Hash className="w-4 h-4 text-indigo-400" />
                Application / Job Filing Number
              </label>
              <div className="relative">
                <Input
                  placeholder="Enter application number (e.g., 123456789)"
                  value={quickSearchAppId}
                  onChange={(e) => setQuickSearchAppId(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleQuickSearch()}
                  className="h-12 text-lg bg-white/5 border-white/20 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 placeholder:text-zinc-600"
                  data-testid="quick-search-input"
                  autoFocus
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-600 text-xs">
                  Press Enter
                </div>
              </div>
            </div>

            {/* Column Visibility Selection */}
            <div className="space-y-3">
              <label className="text-sm font-medium text-zinc-300 flex items-center gap-2">
                <Eye className="w-4 h-4 text-indigo-400" />
                Fields to Display
              </label>
              <div className="grid grid-cols-3 gap-2">
                {Object.entries(COLUMN_CONFIG).map(([key, config]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => toggleColumn(key)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                      visibleColumns[key]
                        ? 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/40'
                        : 'bg-white/5 text-zinc-500 border border-white/10 hover:bg-white/10 hover:text-zinc-300'
                    }`}
                    data-testid={`column-toggle-${key}`}
                  >
                    {visibleColumns[key] ? (
                      <Eye className="w-3.5 h-3.5" />
                    ) : (
                      <EyeOff className="w-3.5 h-3.5" />
                    )}
                    {config.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Quick Tips */}
            <div className="bg-indigo-500/10 border border-indigo-500/20 rounded-lg p-3">
              <p className="text-xs text-indigo-300 flex items-start gap-2">
                <Sparkles className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>
                  <strong>Tip:</strong> You can search by Job Filing Number, Permit ID, or BIS Sequence Number. 
                  Select the fields you want to see in your results.
                </span>
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="relative flex items-center justify-between pt-4 border-t border-white/10">
            <Button
              variant="ghost"
              onClick={skipQuickSearch}
              className="text-zinc-400 hover:text-white"
              data-testid="skip-quick-search"
            >
              Skip & Browse All
            </Button>
            <Button
              onClick={handleQuickSearch}
              disabled={quickSearching}
              className="bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white px-6 shadow-lg shadow-indigo-500/30"
              data-testid="quick-search-btn"
            >
              {quickSearching ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Searching...
                </>
              ) : (
                <>
                  <Search className="w-4 h-4 mr-2" />
                  Search
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <div className="space-y-6" data-testid="applications-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">DOB BIS Applications</h1>
            <p className="text-zinc-400">Track DOB job filings and permit application status</p>
          </div>
          {/* Quick Search Button to reopen dialog */}
          <Button
            onClick={() => setShowQuickSearch(true)}
            className="bg-gradient-to-r from-indigo-500/20 to-purple-500/20 border border-indigo-500/30 hover:from-indigo-500/30 hover:to-purple-500/30 text-indigo-300"
            data-testid="open-quick-search"
          >
            <Zap className="w-4 h-4 mr-2" />
            Quick Search
          </Button>
        </div>

        {/* Filters */}
        <form onSubmit={handleSearch} className="glass-card p-6 rounded-xl">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-4">
            {/* Dataset */}
            <div>
              <label className="block text-sm text-zinc-400 mb-2">Source</label>
              <Select 
                value={filters.dataset} 
                onValueChange={(v) => setFilters({ ...filters, dataset: v })}
              >
                <SelectTrigger className="input-dark">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="permits">DOB Permits</SelectItem>
                  <SelectItem value="dob_now">DOB NOW Filings</SelectItem>
                  <SelectItem value="certificate_of_occupancy">Certificate of Occupancy</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Borough */}
            <div>
              <label className="block text-sm text-zinc-400 mb-2">Borough</label>
              <Select 
                value={filters.borough} 
                onValueChange={(v) => setFilters({ ...filters, borough: v })}
              >
                <SelectTrigger className="input-dark">
                  <SelectValue placeholder="All Boroughs" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Boroughs</SelectItem>
                  <SelectItem value="Manhattan">Manhattan</SelectItem>
                  <SelectItem value="Brooklyn">Brooklyn</SelectItem>
                  <SelectItem value="Queens">Queens</SelectItem>
                  <SelectItem value="Bronx">Bronx</SelectItem>
                  <SelectItem value="Staten Island">Staten Island</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Address with Live Lookup */}
            <div className="lg:col-span-2 relative">
              <label className="block text-sm text-zinc-400 mb-2">Address</label>
              <div className="relative">
                <Input
                  ref={addressInputRef}
                  placeholder="Start typing to search..."
                  value={filters.address}
                  onChange={(e) => setFilters({ ...filters, address: e.target.value })}
                  onFocus={() => addressSuggestions.length > 0 && setShowSuggestions(true)}
                  className="input-dark pr-8"
                  autoComplete="off"
                />
                {loadingSuggestions && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <div className="w-4 h-4 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                )}
              </div>
              
              {showSuggestions && addressSuggestions.length > 0 && (
                <div 
                  ref={suggestionsRef}
                  className="absolute z-50 w-full mt-1 bg-zinc-900 border border-white/10 rounded-lg shadow-xl max-h-60 overflow-y-auto"
                >
                  {addressSuggestions.map((suggestion, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => selectAddress(suggestion)}
                      className="w-full px-4 py-3 text-left hover:bg-white/10 transition-colors flex items-start gap-3 border-b border-white/5 last:border-0"
                    >
                      <MapPin className="w-4 h-4 text-indigo-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-white text-sm font-medium">{suggestion.address}</p>
                        <p className="text-xs text-zinc-500">
                          Block {suggestion.block}, Lot {suggestion.lot} • {suggestion.borough}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Block */}
            <div>
              <label className="block text-sm text-zinc-400 mb-2">Block</label>
              <Input
                placeholder="e.g. 1234"
                value={filters.block}
                onChange={(e) => setFilters({ ...filters, block: e.target.value })}
                className="input-dark"
              />
            </div>

            {/* Lot */}
            <div>
              <label className="block text-sm text-zinc-400 mb-2">Lot</label>
              <Input
                placeholder="e.g. 56"
                value={filters.lot}
                onChange={(e) => setFilters({ ...filters, lot: e.target.value })}
                className="input-dark"
              />
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm text-zinc-400 mb-2">Status</label>
              <Select 
                value={filters.status} 
                onValueChange={(v) => setFilters({ ...filters, status: v })}
              >
                <SelectTrigger className="input-dark">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="Approved">Approved</SelectItem>
                  <SelectItem value="In Progress">In Progress</SelectItem>
                  <SelectItem value="Plan Exam">Plan Exam</SelectItem>
                  <SelectItem value="Under Review">Under Review</SelectItem>
                  <SelectItem value="Pre-Filing">Pre-Filing</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/10">
            <p className="text-sm text-zinc-400">
              {total} application{total !== 1 ? 's' : ''} found
            </p>
            <div className="flex items-center gap-3">
              <Button 
                type="button"
                variant="ghost"
                onClick={() => {
                  setFilters({ dataset: 'permits', address: '', block: '', lot: '', borough: 'all', status: 'all', jobType: 'all', workType: 'all' });
                  setActivePivot('all');
                  setPage(0);
                }}
                className="btn-ghost"
                data-testid="clear-filters-btn"
              >
                <X className="w-4 h-4 mr-2" />
                Clear Filters
              </Button>
              <Button type="submit" className="btn-primary" data-testid="search-applications-btn">
                <Search className="w-4 h-4 mr-2" />
                Search Applications
              </Button>
            </div>
          </div>
        </form>

        {/* Pivot Type Filtering */}
        {rawApplications.length > 0 && (
          <div className="glass-card p-4 rounded-xl" data-testid="pivot-filters">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-indigo-400" />
                <span className="text-sm font-medium text-white">Quick Filter by Type</span>
              </div>
              <div className="flex items-center gap-1 p-1 bg-white/5 rounded-lg">
                <button
                  onClick={() => { setPivotMode('jobType'); setActivePivot('all'); }}
                  className={`px-3 py-1 text-xs font-medium rounded transition-all ${
                    pivotMode === 'jobType' 
                      ? 'bg-indigo-500 text-white' 
                      : 'text-zinc-400 hover:text-white'
                  }`}
                  data-testid="pivot-mode-jobtype"
                >
                  Job Type
                </button>
                <button
                  onClick={() => { setPivotMode('workType'); setActivePivot('all'); }}
                  className={`px-3 py-1 text-xs font-medium rounded transition-all ${
                    pivotMode === 'workType' 
                      ? 'bg-indigo-500 text-white' 
                      : 'text-zinc-400 hover:text-white'
                  }`}
                  data-testid="pivot-mode-worktype"
                >
                  Work Type
                </button>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-2">
              {/* All option */}
              <button
                onClick={() => handlePivotClick('all')}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  activePivot === 'all'
                    ? 'bg-indigo-500 text-white'
                    : 'bg-white/5 text-zinc-400 hover:bg-white/10 hover:text-white border border-white/10'
                }`}
                data-testid="pivot-all"
              >
                All Types
                <span className="ml-1.5 px-1.5 py-0.5 rounded bg-white/10 text-[10px]">
                  {rawApplications.length}
                </span>
              </button>
              
              {/* Dynamic pivot buttons */}
              {pivotOptions.map(([type, count]) => (
                <button
                  key={type}
                  onClick={() => handlePivotClick(type)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    activePivot === type
                      ? 'bg-indigo-500 text-white'
                      : 'bg-white/5 text-zinc-400 hover:bg-white/10 hover:text-white border border-white/10'
                  }`}
                  data-testid={`pivot-${type.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  {type}
                  <span className={`ml-1.5 px-1.5 py-0.5 rounded text-[10px] ${
                    activePivot === type ? 'bg-white/20' : 'bg-white/10'
                  }`}>
                    {count}
                  </span>
                </button>
              ))}
            </div>
            
            {activePivot !== 'all' && (
              <div className="mt-3 pt-3 border-t border-white/10 flex items-center justify-between">
                <p className="text-xs text-zinc-500">
                  Showing <span className="text-indigo-400 font-medium">{sortedApplications.length}</span> applications with {pivotMode === 'jobType' ? 'job type' : 'work type'}: <span className="text-white">{activePivot}</span>
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setActivePivot('all')}
                  className="h-6 px-2 text-xs text-zinc-400 hover:text-white"
                >
                  <X className="w-3 h-3 mr-1" />
                  Clear Filter
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Results Header with Export Options */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-white">
              {activePivot !== 'all' 
                ? `${sortedApplications.length.toLocaleString()} of ${total.toLocaleString()} applications` 
                : `${total.toLocaleString()} applications found`}
            </h2>
            <p className="text-sm text-zinc-500">
              Showing page {page + 1} of {totalPages || 1}
              {activePivot !== 'all' && (
                <span className="ml-2 text-indigo-400">• Filtered by {pivotMode === 'jobType' ? 'job type' : 'work type'}: {activePivot}</span>
              )}
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Export Button - Opens Export Dialog */}
            <Button 
              variant="outline" 
              className="btn-secondary" 
              disabled={exporting || total === 0}
              onClick={() => setExportDialogOpen(true)}
              data-testid="export-btn"
            >
              {exporting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Export
            </Button>
          </div>
        </div>

        {/* Export Dialog */}
        <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
          <DialogContent className="glass-card border-white/10 sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <Download className="w-5 h-5 text-indigo-400" />
                Export Data
              </DialogTitle>
              <DialogDescription className="text-zinc-400">
                Choose your export format and save location
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6 mt-4">
              {/* Large dataset warning */}
              {total > 1000 && (
                <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 p-3 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-300">Large Dataset ({total.toLocaleString()} records)</p>
                    <p className="text-xs text-amber-200/70 mt-1">
                      Exporting all results may take a while. Consider using "Current Page" for faster exports.
                    </p>
                  </div>
                </div>
              )}
              
              {/* Export Scope Selection */}
              <div className="space-y-3">
                <Label className="text-sm font-medium text-zinc-300">Export Scope</Label>
                <RadioGroup 
                  value={exportScope} 
                  onValueChange={setExportScope}
                  className="grid grid-cols-2 gap-3"
                >
                  <div>
                    <RadioGroupItem value="page" id="scope-page" className="peer sr-only" />
                    <Label
                      htmlFor="scope-page"
                      className="flex flex-col items-center justify-center rounded-lg border-2 border-white/10 bg-white/5 p-3 hover:bg-white/10 hover:border-white/20 peer-data-[state=checked]:border-cyan-500 peer-data-[state=checked]:bg-cyan-500/10 cursor-pointer transition-all"
                    >
                      <span className="text-sm font-medium text-white">Current Page</span>
                      <span className="text-xs text-zinc-500">{applications.length} records</span>
                    </Label>
                  </div>
                  <div>
                    <RadioGroupItem value="all" id="scope-all" className="peer sr-only" />
                    <Label
                      htmlFor="scope-all"
                      className="flex flex-col items-center justify-center rounded-lg border-2 border-white/10 bg-white/5 p-3 hover:bg-white/10 hover:border-white/20 peer-data-[state=checked]:border-indigo-500 peer-data-[state=checked]:bg-indigo-500/10 cursor-pointer transition-all"
                    >
                      <span className="text-sm font-medium text-white">All Results</span>
                      <span className="text-xs text-zinc-500">Up to 1,000 records</span>
                    </Label>
                  </div>
                </RadioGroup>
              </div>
              
              {/* Format Selection */}
              <div className="space-y-3">
                <Label className="text-sm font-medium text-zinc-300">Export Format</Label>
                <RadioGroup 
                  value={exportFormat} 
                  onValueChange={setExportFormat}
                  className="grid grid-cols-3 gap-3"
                >
                  <div>
                    <RadioGroupItem value="csv" id="csv" className="peer sr-only" />
                    <Label
                      htmlFor="csv"
                      className="flex flex-col items-center justify-center rounded-lg border-2 border-white/10 bg-white/5 p-4 hover:bg-white/10 hover:border-white/20 peer-data-[state=checked]:border-emerald-500 peer-data-[state=checked]:bg-emerald-500/10 cursor-pointer transition-all"
                    >
                      <FileSpreadsheet className="w-8 h-8 mb-2 text-emerald-400" />
                      <span className="text-sm font-medium text-white">CSV</span>
                      <span className="text-xs text-zinc-500">Spreadsheet</span>
                    </Label>
                  </div>
                  <div>
                    <RadioGroupItem value="xls" id="xls" className="peer sr-only" />
                    <Label
                      htmlFor="xls"
                      className="flex flex-col items-center justify-center rounded-lg border-2 border-white/10 bg-white/5 p-4 hover:bg-white/10 hover:border-white/20 peer-data-[state=checked]:border-blue-500 peer-data-[state=checked]:bg-blue-500/10 cursor-pointer transition-all"
                    >
                      <FileSpreadsheet className="w-8 h-8 mb-2 text-blue-400" />
                      <span className="text-sm font-medium text-white">XLS</span>
                      <span className="text-xs text-zinc-500">Excel</span>
                    </Label>
                  </div>
                  <div>
                    <RadioGroupItem value="pdf" id="pdf" className="peer sr-only" />
                    <Label
                      htmlFor="pdf"
                      className="flex flex-col items-center justify-center rounded-lg border-2 border-white/10 bg-white/5 p-4 hover:bg-white/10 hover:border-white/20 peer-data-[state=checked]:border-rose-500 peer-data-[state=checked]:bg-rose-500/10 cursor-pointer transition-all"
                    >
                      <FileText className="w-8 h-8 mb-2 text-rose-400" />
                      <span className="text-sm font-medium text-white">PDF</span>
                      <span className="text-xs text-zinc-500">Report</span>
                    </Label>
                  </div>
                </RadioGroup>
              </div>
              
              {/* Location Selection */}
              <div className="space-y-3">
                <Label className="text-sm font-medium text-zinc-300">Save Location</Label>
                <RadioGroup 
                  value={exportLocation} 
                  onValueChange={setExportLocation}
                  className="space-y-2"
                >
                  {/* Downloads Folder - Always available */}
                  <div className="flex items-center space-x-3 rounded-lg border border-white/10 bg-white/5 p-3 hover:bg-white/10 cursor-pointer transition-all">
                    <RadioGroupItem value="download" id="download" />
                    <Label htmlFor="download" className="flex items-center gap-3 cursor-pointer flex-1">
                      <HardDrive className="w-5 h-5 text-indigo-400" />
                      <div>
                        <p className="text-sm font-medium text-white">Downloads Folder</p>
                        <p className="text-xs text-zinc-500">Save to browser&apos;s default download location</p>
                      </div>
                    </Label>
                  </div>
                  
                  {/* Choose Location - Only show if supported */}
                  {supportsFilePicker && (
                    <div className="flex items-center space-x-3 rounded-lg border border-white/10 bg-white/5 p-3 hover:bg-white/10 cursor-pointer transition-all">
                      <RadioGroupItem value="choose" id="choose" />
                      <Label htmlFor="choose" className="flex items-center gap-3 cursor-pointer flex-1">
                        <FolderOpen className="w-5 h-5 text-amber-400" />
                        <div>
                          <p className="text-sm font-medium text-white">Choose Location</p>
                          <p className="text-xs text-zinc-500">Select folder using file picker dialog</p>
                        </div>
                      </Label>
                    </div>
                  )}
                  
                  {/* Email Export - Always available */}
                  <div className="flex items-center space-x-3 rounded-lg border border-white/10 bg-white/5 p-3 hover:bg-white/10 cursor-pointer transition-all">
                    <RadioGroupItem value="email" id="email" />
                    <Label htmlFor="email" className="flex items-center gap-3 cursor-pointer flex-1">
                      <Mail className="w-5 h-5 text-emerald-400" />
                      <div>
                        <p className="text-sm font-medium text-white">Send via Email</p>
                        <p className="text-xs text-zinc-500">Receive export file in your inbox</p>
                      </div>
                    </Label>
                  </div>
                </RadioGroup>
                
                {/* Email input field */}
                {exportLocation === 'email' && (
                  <div className="mt-3">
                    <Input
                      type="email"
                      placeholder="Enter email address"
                      value={exportEmail}
                      onChange={(e) => setExportEmail(e.target.value)}
                      className="bg-white/5 border-white/10 text-white placeholder:text-zinc-500"
                    />
                  </div>
                )}
                
                {/* Browser compatibility notice */}
                {!supportsFilePicker && (
                  <p className="text-xs text-zinc-500 flex items-center gap-1 mt-2">
                    <AlertCircle className="w-3 h-3" />
                    Tip: Use Chrome or Edge for &quot;Choose Location&quot; file picker support
                  </p>
                )}
              </div>
              
              {/* Export Summary */}
              <div className="rounded-lg bg-white/5 border border-white/10 p-3">
                <p className="text-xs text-zinc-400">
                  Exporting <span className="text-white font-medium">
                    {exportScope === 'page' 
                      ? applications.length.toLocaleString() 
                      : Math.min(total, 1000).toLocaleString()
                    }
                  </span> records 
                  {exportScope === 'all' && total > 1000 && <span className="text-amber-400"> (max 1,000)</span>}
                  {exportScope === 'page' && <span className="text-cyan-400"> (current page)</span>}
                  {' '}as <span className="text-white font-medium">{exportFormat.toUpperCase()}</span>
                  {exportLocation === 'email' && exportEmail && (
                    <span> to <span className="text-emerald-400">{exportEmail}</span></span>
                  )}
                </p>
              </div>
              
              {/* Action Buttons */}
              <div className="flex justify-between items-center pt-2">
                <Button 
                  variant="ghost" 
                  onClick={() => setTemplateDialogOpen(true)}
                  className="text-zinc-400 hover:text-white text-sm"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Use Custom Template
                </Button>
                <div className="flex gap-2">
                  <Button 
                    variant="ghost" 
                    onClick={() => setExportDialogOpen(false)}
                    className="btn-ghost"
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleExport}
                    disabled={exporting}
                    className="btn-primary"
                  >
                    {exporting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Exporting...
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4 mr-2" />
                        Export
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Custom Template Dialog */}
        <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
          <DialogContent className="glass-card border-white/10">
            <DialogHeader>
              <DialogTitle className="text-white">Export with Custom Template</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <p className="text-sm text-zinc-400">
                Upload your own Excel template. The data will be populated into your template format.
              </p>
              
              <div className="border-2 border-dashed border-white/20 rounded-lg p-6 text-center">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleTemplateUpload}
                  accept=".xlsx,.xls,.csv"
                  className="hidden"
                />
                {uploadedTemplate ? (
                  <div className="space-y-2">
                    <FileSpreadsheet className="w-12 h-12 mx-auto text-emerald-400" />
                    <p className="text-white font-medium">{uploadedTemplate.name}</p>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      className="text-zinc-400 hover:text-white"
                    >
                      Change template
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Upload className="w-12 h-12 mx-auto text-zinc-500" />
                    <p className="text-zinc-400">Drop your template here or</p>
                    <Button 
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      className="btn-secondary"
                    >
                      Browse Files
                    </Button>
                    <p className="text-xs text-zinc-500">.xlsx, .xls, or .csv files</p>
                  </div>
                )}
              </div>
              
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => setTemplateDialogOpen(false)} className="btn-ghost">
                  Cancel
                </Button>
                <Button 
                  onClick={exportWithTemplate} 
                  disabled={!uploadedTemplate || exporting}
                  className="btn-primary"
                >
                  {exporting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Exporting...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4 mr-2" />
                      Export with Template
                    </>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Results Table */}
        <div className="glass-card rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  {visibleColumns.permit_id && (
                    <th className="cursor-pointer hover:text-zinc-300 transition-colors" onClick={() => handleSort('permit_id')}>
                      <span className="flex items-center gap-1">Application ID <SortIndicator column="permit_id" sortConfig={sortConfig} /></span>
                    </th>
                  )}
                  {visibleColumns.address && (
                    <th className="cursor-pointer hover:text-zinc-300 transition-colors" onClick={() => handleSort('address')}>
                      <span className="flex items-center gap-1">Address <SortIndicator column="address" sortConfig={sortConfig} /></span>
                    </th>
                  )}
                  {visibleColumns.aka && <th>AKA</th>}
                  {visibleColumns.block_lot && (
                    <th className="cursor-pointer hover:text-zinc-300 transition-colors" onClick={() => handleSort('block_lot')}>
                      <span className="flex items-center gap-1">Block/Lot <SortIndicator column="block_lot" sortConfig={sortConfig} /></span>
                    </th>
                  )}
                  {visibleColumns.job_type && (
                    <th className="cursor-pointer hover:text-zinc-300 transition-colors" onClick={() => handleSort('job_type')}>
                      <span className="flex items-center gap-1">Job Type <SortIndicator column="job_type" sortConfig={sortConfig} /></span>
                    </th>
                  )}
                  {visibleColumns.work_type && (
                    <th className="cursor-pointer hover:text-zinc-300 transition-colors" onClick={() => handleSort('work_type')}>
                      <span className="flex items-center gap-1">Work Type <SortIndicator column="work_type" sortConfig={sortConfig} /></span>
                    </th>
                  )}
                  {visibleColumns.description && (
                    <th className="cursor-pointer hover:text-zinc-300 transition-colors" onClick={() => handleSort('description')}>
                      <span className="flex items-center gap-1">Description <SortIndicator column="description" sortConfig={sortConfig} /></span>
                    </th>
                  )}
                  {visibleColumns.estimated_cost && (
                    <th className="cursor-pointer hover:text-zinc-300 transition-colors" onClick={() => handleSort('estimated_cost')}>
                      <span className="flex items-center gap-1">Est. Cost <SortIndicator column="estimated_cost" sortConfig={sortConfig} /></span>
                    </th>
                  )}
                  {visibleColumns.issue_date && (
                    <th className="cursor-pointer hover:text-zinc-300 transition-colors" onClick={() => handleSort('issue_date')}>
                      <span className="flex items-center gap-1">Filed Date <SortIndicator column="issue_date" sortConfig={sortConfig} /></span>
                    </th>
                  )}
                  {visibleColumns.status && (
                    <th className="cursor-pointer hover:text-zinc-300 transition-colors" onClick={() => handleSort('status')}>
                      <span className="flex items-center gap-1">Status <SortIndicator column="status" sortConfig={sortConfig} /></span>
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={Object.values(visibleColumns).filter(Boolean).length || 9} className="text-center py-8">
                      <div className="spinner mx-auto" />
                    </td>
                  </tr>
                ) : sortedApplications.length === 0 ? (
                  <tr>
                    <td colSpan={Object.values(visibleColumns).filter(Boolean).length || 9} className="text-center py-8 text-zinc-500">
                      No applications found. Try adjusting your filters.
                    </td>
                  </tr>
                ) : (
                  sortedApplications.map((app, i) => (
                    <tr key={i} className={app.is_aka ? 'bg-amber-500/5' : ''}>
                      {visibleColumns.permit_id && (
                        <td className="mono text-xs">{app.permit_id}</td>
                      )}
                      {visibleColumns.address && (
                        <td>
                          <div className="flex items-center gap-2">
                            <MapPin className={`w-4 h-4 flex-shrink-0 ${app.is_aka ? 'text-amber-400' : 'text-indigo-400'}`} />
                            <span>{app.address}</span>
                          </div>
                        </td>
                      )}
                      {visibleColumns.aka && (
                        <td>
                          {app.is_aka ? (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger>
                                  <span className="inline-flex items-center gap-1 text-xs bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded">
                                    <Link2 className="w-3 h-3" />
                                    AKA
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p className="text-xs">{app.aka_note || 'Alternate address for this property'}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ) : (
                            <span className="text-xs text-zinc-500">Primary</span>
                          )}
                        </td>
                      )}
                      {visibleColumns.block_lot && (
                        <td>{app.block}/{app.lot}</td>
                      )}
                      {visibleColumns.job_type && (
                        <td>
                          <span className="text-xs bg-indigo-500/20 text-indigo-400 px-2 py-0.5 rounded">
                            {app.job_type || '-'}
                          </span>
                        </td>
                      )}
                      {visibleColumns.work_type && (
                        <td>
                          <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded">
                            {app.work_type || '-'}
                          </span>
                        </td>
                      )}
                      {visibleColumns.description && (
                        <td className="max-w-xs truncate" title={app.description}>{app.description}</td>
                      )}
                      {visibleColumns.estimated_cost && (
                        <td className="text-right">
                          {app.estimated_cost ? (
                            <span className="text-emerald-400 font-medium">
                              {String(app.estimated_cost).startsWith('$') 
                                ? app.estimated_cost 
                                : `$${Number(app.estimated_cost).toLocaleString()}`}
                            </span>
                          ) : (
                            <span className="text-zinc-500">-</span>
                          )}
                        </td>
                      )}
                      {visibleColumns.issue_date && (
                        <td>{app.issue_date}</td>
                      )}
                      {visibleColumns.status && (
                        <td>
                          <span className={`flex items-center gap-1.5 ${getStatusBadge(app.status)}`}>
                            {getStatusIcon(app.status)}
                            {app.status}
                          </span>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between p-4 border-t border-white/10">
            <div className="flex items-center gap-4">
              <p className="text-sm text-zinc-400">
                Page {page + 1} of {totalPages || 1}
              </p>
              
              {/* Page Size Selector */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-zinc-500">Show</span>
                <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
                  <SelectTrigger className="w-20 h-8 bg-white/5 border-white/10 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/10">
                    {pageSizeOptions.map(size => (
                      <SelectItem key={size} value={String(size)} className="text-zinc-300">
                        {size}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="text-sm text-zinc-500">per page</span>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              
              {/* Page number buttons */}
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i;
                  } else if (page < 3) {
                    pageNum = i;
                  } else if (page > totalPages - 4) {
                    pageNum = totalPages - 5 + i;
                  } else {
                    pageNum = page - 2 + i;
                  }
                  return (
                    <Button
                      key={pageNum}
                      variant={page === pageNum ? "default" : "ghost"}
                      size="sm"
                      onClick={() => setPage(pageNum)}
                      className={page === pageNum ? "bg-indigo-600 text-white" : "text-zinc-400"}
                    >
                      {pageNum + 1}
                    </Button>
                  );
                })}
              </div>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
